/**
 * 智慧无限 macro_tab（2048 字节）编解码
 * 协议：0x1B 读 / 0x1C 写；动作格式见 V1.4「宏信息格式」sheet
 */

import { getTabScopedEquipmentData } from "../driverTabDevice";

export const ZHWX_MACRO_SLOT_COUNT = 20;
export const ZHWX_MACRO_TAB_SIZE = 2048;
export const ZHWX_MACRO_HEADER_SIZE = 8;
export const ZHWX_MACRO_ADDR_OFFSET = 8;
export const ZHWX_MACRO_DATA_BASE =
  ZHWX_MACRO_ADDR_OFFSET + ZHWX_MACRO_SLOT_COUNT * 2;

/** 公版 macro_tab 有效魔数（offset 0~1），有宏数据时固定写 0x55AA */
export const ZHWX_MACRO_TAB_MAGIC = 0x55aa;

/**
 * 单宏槽数据区（协议 sheet「宏信息格式」）：
 * u16 动作数量 + u16 保留 + 4B×N。
 * 4B 为 [delayL][delayH][keyValue][actByte]。
 * actByte bit7：1=按下，0=弹起；低 7 bit 为按键类型（键盘普通键=0x01）。
 * 动作必须从槽地址 +4 开始。紧跟在数量字段后面写时，固件把延时当成保留字节，读回一致但不播放。
 * 例：Q 按下 81ms → 51 00 14 81；Q 弹起 → xx xx 14 01。
 */
export const ZHWX_MACRO_SLOT_HEADER_SIZE = 4;

/**
 * 公版 PC 工具槽头：addr 处 u16 动作数常为 0，后接 6 字节保留，标准 4B 动作从 addr+8 开始。
 * 例：0x30 处 `00 00` + 6B padding，0x38 起 `9e 00 14 01`（Q 按下）。
 */
export const ZHWX_OFFICIAL_MACRO_SLOT_PREFIX_SIZE = 8;

/** 宏动作：键盘普通键 */
export const ZHWX_MACRO_ACT_KB = 0x01;
/** 宏动作：键盘热键（Ctrl/Shift 等） */
export const ZHWX_MACRO_ACT_HOTKEY = 0x02;
/** 宏动作：鼠标键 */
export const ZHWX_MACRO_ACT_MOUSE = 0x03;

/**
 * 改键面板宏键 UI 键码基址：M0=62720、M1=62721 …
 * 与 macro_tab 槽位 macroId 一致（0 基，M0→macroId=0→矩阵 codeL=0）。
 * 公版 PC 工具界面虽标 M1 起，协议地址表 slot0 即第一个宏。
 */
/**
 * UI 改键宏虚拟键码基址：M0=62720、M1=62721…
 * 与协议 macro_tab 槽号一致：M0 → macroId/slot=0 → 矩阵绑键 `50 00 xx`（codeL=0）
 * 公版 PC 工具界面虽显示 M1 起，Flash 地址表仍从 slot0 存第一组宏。
 */
export const ZHWX_MACRO_UI_BASE = 62720;

/** 间隔模式：0 默认（录制延时） / 1 固定延时 */
export type ZhwxMacroIntervalMode = 0 | 1;

/** 改键绑定：0x50 停止方式 / 0x51 播放次数（互斥） */
export type ZhwxMacroBindMode = "stop" | "count";

/** 0x50 停止方式：见协议 sheet6 */
export type ZhwxMacroStopMethod = 0 | 1 | 2 | 3;

export type ZhwxMacroSlotPref = {
  intervalMode: ZhwxMacroIntervalMode;
  fixedIntervalMs: number;
  bindMode: ZhwxMacroBindMode;
  stopMethod: ZhwxMacroStopMethod;
  playCount: number;
};

const DEFAULT_ZHWX_MACRO_SLOT_PREF: ZhwxMacroSlotPref = {
  intervalMode: 0,
  fixedIntervalMs: 1,
  bindMode: "stop",
  stopMethod: 0,
  playCount: 1,
};

export type ZhwxMacroAction = {
  delay: number;
  status: number;
  actionType: number;
  keyCode: number;
};

export type ZhwxMacroSlotSummary = {
  macroId: number;
  valid: boolean;
  actNum: number;
  intervalMode: ZhwxMacroIntervalMode;
  fixedIntervalMs: number;
};

export type ZhwxParsedMacroTab = {
  raw: Uint8Array;
  slots: Array<{
    macroId: number;
    valid: boolean;
    actions: ZhwxMacroAction[];
    intervalMode: ZhwxMacroIntervalMode;
    fixedIntervalMs: number;
  }>;
};

const HOTKEY_UI_TO_MASK: Readonly<Record<number, number>> = {
  224: 0x01,
  225: 0x02,
  226: 0x04,
  227: 0x08,
  228: 0x10,
  229: 0x20,
  230: 0x40,
  231: 0x80,
};

const HOTKEY_MASK_TO_UI: Readonly<Record<number, number>> = {
  0x01: 224,
  0x02: 225,
  0x04: 226,
  0x08: 227,
  0x10: 228,
  0x20: 229,
  0x40: 230,
  0x80: 231,
};

/** UI 鼠标码 → 协议 MS 键值（与 keyboard_key_define.h 一致） */
const MOUSE_UI_TO_CODE: Readonly<Record<number, number>> = {
  16640: 0x01,
  16896: 0x02,
  17152: 0x04,
};

const MOUSE_CODE_TO_UI: Readonly<Record<number, number>> = {
  0x01: 16640,
  0x02: 16896,
  0x04: 17152,
};

function readU16(data: Uint8Array, offset: number): number {
  return (data[offset] ?? 0) | ((data[offset + 1] ?? 0) << 8);
}

function writeU16(data: Uint8Array, offset: number, value: number): void {
  data[offset] = value & 0xff;
  data[offset + 1] = (value >> 8) & 0xff;
}

/** 宏槽是否有效：优先看地址表；header 0~1 的 bit 表示槽占用（可与 0x55AA 魔数共存） */
function isSlotUsed(data: Uint8Array, macroId: number): boolean {
  const addr = readU16(data, ZHWX_MACRO_ADDR_OFFSET + macroId * 2);
  if (addr >= ZHWX_MACRO_DATA_BASE) return true;

  const header = readU16(data, 0);
  if (header === 0) return false;

  if (macroId < 16) {
    return ((header >> macroId) & 1) === 1;
  }
  if (macroId < ZHWX_MACRO_SLOT_COUNT) {
    const bit = macroId - 16;
    return ((data[6] ?? 0) >> bit) & 1 ? true : false;
  }
  return false;
}

/** 完整按下/抬起成对序列（偶数条，同键 press→release 交替） */
function isStandardFullMacroActions(actions: ZhwxMacroAction[]): boolean {
  if (actions.length < 2 || actions.length % 2 !== 0) return false;
  for (let i = 0; i < actions.length; i += 2) {
    const press = actions[i];
    const release = actions[i + 1];
    if (!press || !release) return false;
    if (press.status !== 1 || release.status !== 0) return false;
    if (press.keyCode !== release.keyCode) return false;
  }
  return true;
}

function scoreParsedMacroActions(
  actions: ZhwxMacroAction[],
  declaredCount = 0
): number {
  if (!actions.length) return -1;
  let score = actions.length * 10;
  if (declaredCount > 0 && actions.length === declaredCount) score += 200;
  if (isStandardFullMacroActions(actions)) score += 100;
  else if (isOfficialCompressedMacroActions(actions)) score += 40;
  return score;
}

function scanStandardMacroActions(
  data: Uint8Array,
  start: number,
  bodyEnd: number,
  standardActionsOnly: boolean
): ZhwxMacroAction[] {
  const scanned: ZhwxMacroAction[] = [];
  let cursor = start;
  while (cursor + ZHWX_MACRO_ACTION_RECORD_SIZE <= bodyEnd) {
    if (isStandardMacroActionRecord(data, cursor)) {
      const act = parseStandardActionRecord(data, cursor);
      if (!act) break;
      scanned.push(act);
      cursor += ZHWX_MACRO_ACTION_RECORD_SIZE;
      continue;
    }
    if (standardActionsOnly) break;
    if (isInterleavedMacroActionRecord(data, cursor)) {
      const act = parseInterleavedActionRecord(data, cursor);
      if (!act) break;
      scanned.push(act);
      cursor += ZHWX_MACRO_ACTION_RECORD_SIZE;
      continue;
    }
    if (isStaggeredMacroActionRecord(data, cursor)) {
      const act = parseStaggeredActionRecord(data, cursor);
      if (!act) break;
      scanned.push(act);
      cursor += ZHWX_MACRO_ACTION_RECORD_SIZE;
      continue;
    }
    break;
  }
  return scanned;
}

function scanMacroBodyWithFallbacks(
  data: Uint8Array,
  offset: number,
  bodyEnd: number,
  standardActionsOnly: boolean,
  declaredCount = 0
): { actions: ZhwxMacroAction[]; bytesUsed: number } {
  const starts = [
    offset + ZHWX_MACRO_SLOT_HEADER_SIZE,
    offset + 2,
    offset + ZHWX_OFFICIAL_MACRO_SLOT_PREFIX_SIZE,
    offset,
  ];
  let best: {
    actions: ZhwxMacroAction[];
    bytesUsed: number;
    score: number;
  } | null = null;

  for (const start of starts) {
    const scanned = scanStandardMacroActions(
      data,
      start,
      bodyEnd,
      standardActionsOnly
    );
    const score = scoreParsedMacroActions(scanned, declaredCount);
    if (score < 0) continue;
    const candidate = {
      actions: scanned,
      bytesUsed:
        Math.max(0, start - offset) +
        scanned.length * ZHWX_MACRO_ACTION_RECORD_SIZE,
      score,
    };
    if (!best || candidate.score > best.score) {
      best = candidate;
    }
  }

  if (!best) return { actions: [], bytesUsed: 0 };
  return { actions: best.actions, bytesUsed: best.bytesUsed };
}

/** 设备 macro_tab 中指定槽是否已有可播放的宏数据（不依赖动作字节序解析） */
export function isZhwxMacroSlotReadyOnTab(
  tab: Uint8Array,
  macroId: number
): boolean {
  if (macroId < 0 || macroId >= ZHWX_MACRO_SLOT_COUNT) return false;
  if (!isSlotUsed(tab, macroId)) return false;
  const addr = readU16(tab, ZHWX_MACRO_ADDR_OFFSET + macroId * 2);
  if (addr < ZHWX_MACRO_DATA_BASE || addr + 2 > tab.length) return false;
  const bodyEnd = resolveMacroSlotBodyEnd(tab, macroId, addr);
  const actCount = readU16(tab, addr);
  if (actCount > 0 && actCount <= 512) {
    if (
      isStandardMacroActionRecord(tab, addr + ZHWX_MACRO_SLOT_HEADER_SIZE) ||
      isStandardMacroActionRecord(tab, addr + 2)
    ) {
      return true;
    }
  }
  return (
    scanMacroBodyWithFallbacks(tab, addr, bodyEnd, true).actions.length > 0
  );
}

/** 从 macro_tab 解析单槽动作（读设备回显 / 绑键校验） */
export function parseZhwxMacroSlotActionsFromTab(
  tab: Uint8Array,
  macroId: number,
  options?: ParseMacroBodyOptions
): ZhwxMacroAction[] {
  if (macroId < 0 || macroId >= ZHWX_MACRO_SLOT_COUNT) return [];
  const addr = readU16(tab, ZHWX_MACRO_ADDR_OFFSET + macroId * 2);
  if (!isSlotUsed(tab, macroId) || addr < ZHWX_MACRO_DATA_BASE) return [];
  const bodyEnd = resolveMacroSlotBodyEnd(tab, macroId, addr);
  return parseMacroBody(tab, addr, bodyEnd, options).actions;
}

function uiKeyToMacroKeyType(keyCode: number, actionType: number) {
  if (actionType === 1 || MOUSE_UI_TO_CODE[keyCode] != null) {
    return {
      keyType: ZHWX_MACRO_ACT_MOUSE,
      keyValue: MOUSE_UI_TO_CODE[keyCode] ?? keyCode & 0xff,
    };
  }
  const hotkey = HOTKEY_UI_TO_MASK[keyCode];
  if (hotkey != null) {
    return { keyType: ZHWX_MACRO_ACT_HOTKEY, keyValue: hotkey };
  }
  // 与改键矩阵一致：普通键 HID 在 codeH，虚拟键盘 UI 键码 ≤255 时与 HID 相同（如 Q=20=0x14）
  const hid = keyCode & 0xff;
  return { keyType: ZHWX_MACRO_ACT_KB, keyValue: hid };
}

function macroKeyToUi(
  keyType: number,
  keyValue: number,
  fallbackActionType: number
) {
  if (keyType === ZHWX_MACRO_ACT_MOUSE) {
    return {
      actionType: 1,
      keyCode: MOUSE_CODE_TO_UI[keyValue] ?? keyValue,
    };
  }
  if (keyType === ZHWX_MACRO_ACT_HOTKEY) {
    return {
      actionType: 3,
      keyCode: HOTKEY_MASK_TO_UI[keyValue] ?? keyValue,
    };
  }
  return {
    actionType: fallbackActionType === 1 ? 1 : 3,
    keyCode: keyValue,
  };
}

/** 单步动作 4 字节（DK87 公版）：[delayL][delayH][keyValue][actByte] */
export const ZHWX_MACRO_ACTION_RECORD_SIZE = 4;

/** 键盘普通键 HID 合法范围（keyboard_key_define.h KEY_A~KEY_F24） */
const ZHWX_MACRO_KB_KEY_MIN = 0x04;
const ZHWX_MACRO_KB_KEY_MAX = 0x73;

function isPlausibleMacroKbKeyValue(keyValue: number): boolean {
  return keyValue >= ZHWX_MACRO_KB_KEY_MIN && keyValue <= ZHWX_MACRO_KB_KEY_MAX;
}

/** sheet7 公版 4B 动作：[delayL, delayH, keyValue, actByte] */
function isStandardMacroActionRecord(
  data: Uint8Array,
  offset: number
): boolean {
  if (offset + ZHWX_MACRO_ACTION_RECORD_SIZE > data.length) return false;
  const actByte = data[offset + 3] ?? 0;
  const keyType = actByte & 0x7f;
  if (keyType < ZHWX_MACRO_ACT_KB || keyType > 0x07) return false;
  const keyValue = data[offset + 2] ?? 0;
  if (keyType === ZHWX_MACRO_ACT_KB && !isPlausibleMacroKbKeyValue(keyValue)) {
    return false;
  }
  const delay = readU16(data, offset);
  if (delay > 60000) return false;
  return true;
}

/** 误写格式（delayH 与 key 对调）：[delayL, actByte, delayH, keyValue] */
function isStaggeredMacroActionRecord(
  data: Uint8Array,
  offset: number
): boolean {
  if (offset + ZHWX_MACRO_ACTION_RECORD_SIZE > data.length) return false;
  const actByte = data[offset + 1] ?? 0;
  const keyType = actByte & 0x7f;
  if (keyType < ZHWX_MACRO_ACT_KB || keyType > 0x07) return false;
  const keyValue = data[offset + 3] ?? 0;
  if (keyType === ZHWX_MACRO_ACT_KB && !isPlausibleMacroKbKeyValue(keyValue)) {
    return false;
  }
  const delay = (data[offset] ?? 0) | ((data[offset + 2] ?? 0) << 8);
  if (delay > 60000) return false;
  return true;
}

/** DK87 4B 动作（sheet7 + 标准 act）：[delayL, actByte, keyValue, delayH] */
function isInterleavedMacroActionRecord(
  data: Uint8Array,
  offset: number
): boolean {
  if (offset + ZHWX_MACRO_ACTION_RECORD_SIZE > data.length) return false;
  const actByte = data[offset + 1] ?? 0;
  const keyType = actByte & 0x7f;
  if (keyType < ZHWX_MACRO_ACT_KB || keyType > 0x07) return false;
  const keyValue = data[offset + 2] ?? 0;
  if (keyType === ZHWX_MACRO_ACT_KB && !isPlausibleMacroKbKeyValue(keyValue)) {
    return false;
  }
  const delay = (data[offset] ?? 0) | ((data[offset + 3] ?? 0) << 8);
  if (delay > 60000) return false;
  return true;
}

/** 兼容旧版 5B 动作（delay + status(0x80) + key + type） */
function isLegacyMacroActionRecord(data: Uint8Array, offset: number): boolean {
  if (offset + 5 > data.length) return false;
  const keyType = data[offset + 4] ?? 0;
  if (keyType < ZHWX_MACRO_ACT_KB || keyType > 0x07) return false;
  const statusByte = data[offset + 2] ?? 0;
  if (statusByte !== 0x00 && statusByte !== 0x80) return false;
  const delay = readU16(data, offset);
  if (delay > 60000) return false;
  return true;
}

/** 协议 actByte：bit7=1 按下，bit7=0 弹起。低 7 bit 为按键类型。 */
function standardActByteToStatus(actByte: number): number {
  return actByte & 0x80 ? 1 : 0;
}

function standardStatusToActByte(status: number, keyType: number): number {
  const typeBits = keyType & 0x7f;
  return status ? typeBits | 0x80 : typeBits;
}

function parseStandardActionRecord(
  data: Uint8Array,
  offset: number
): ZhwxMacroAction | null {
  if (offset + ZHWX_MACRO_ACTION_RECORD_SIZE > data.length) return null;
  const delay = readU16(data, offset);
  const keyValue = data[offset + 2] ?? 0;
  const actByte = data[offset + 3] ?? 0;
  const keyType = actByte & 0x7f;
  const status = standardActByteToStatus(actByte);
  const ui = macroKeyToUi(keyType, keyValue, 3);
  return {
    delay,
    status,
    actionType: ui.actionType,
    keyCode: ui.keyCode,
  };
}

function parseStaggeredActionRecord(
  data: Uint8Array,
  offset: number
): ZhwxMacroAction | null {
  if (offset + ZHWX_MACRO_ACTION_RECORD_SIZE > data.length) return null;
  const delay = (data[offset] ?? 0) | ((data[offset + 2] ?? 0) << 8);
  const actByte = data[offset + 1] ?? 0;
  const keyValue = data[offset + 3] ?? 0;
  const keyType = actByte & 0x7f;
  const status = standardActByteToStatus(actByte);
  const ui = macroKeyToUi(keyType, keyValue, 3);
  return {
    delay,
    status,
    actionType: ui.actionType,
    keyCode: ui.keyCode,
  };
}

function parseInterleavedActionRecord(
  data: Uint8Array,
  offset: number
): ZhwxMacroAction | null {
  if (offset + ZHWX_MACRO_ACTION_RECORD_SIZE > data.length) return null;
  const delay = (data[offset] ?? 0) | ((data[offset + 3] ?? 0) << 8);
  const actByte = data[offset + 1] ?? 0;
  const keyValue = data[offset + 2] ?? 0;
  const keyType = actByte & 0x7f;
  const status = standardActByteToStatus(actByte);
  const ui = macroKeyToUi(keyType, keyValue, 3);
  return {
    delay,
    status,
    actionType: ui.actionType,
    keyCode: ui.keyCode,
  };
}

function parseLegacyActionRecord(
  data: Uint8Array,
  offset: number
): ZhwxMacroAction | null {
  if (offset + 5 > data.length) return null;
  const delay = readU16(data, offset);
  const statusByte = data[offset + 2] ?? 0;
  const keyValue = data[offset + 3] ?? 0;
  const keyType = data[offset + 4] ?? 0;
  const status = statusByte & 0x80 ? 1 : 0;
  const ui = macroKeyToUi(keyType, keyValue, 3);
  return {
    delay,
    status,
    actionType: ui.actionType,
    keyCode: ui.keyCode,
  };
}

/**
 * 公版 macro_tab 压缩格式（Bus Hound 0x1C 写宏抓包）：
 * N 个键 → 2N−1 条 4B 记录，槽头 u16 动作数常为 0，动作从 addr+8 起。
 * - 偶数位：按下 (act=0x01)，delay=按住时长
 * - 奇数位：过渡 (act=0x81)，key 字段=下一键 HID，delay=上一键抬起到下一键按下的间隔
 * - 末条：最后一键按下 delay=1ms，末键抬起由固件隐式处理
 */
export function isOfficialCompressedMacroActions(
  actions: ZhwxMacroAction[]
): boolean {
  if (actions.length < 3 || actions.length % 2 === 0) return false;
  for (let i = 0; i < actions.length; i++) {
    const rec = actions[i];
    if (i % 2 === 0) {
      if (rec.status !== 1) return false;
      continue;
    }
    if (rec.status !== 0) return false;
    const next = actions[i + 1];
    if (!next || next.status !== 1 || next.keyCode !== rec.keyCode) {
      return false;
    }
  }
  return actions[actions.length - 1]?.status === 1;
}

/** 将公版压缩宏展开为 UI 用的完整按下/抬起序列 */
export function expandOfficialCompressedMacroActions(
  actions: ZhwxMacroAction[]
): ZhwxMacroAction[] {
  if (!isOfficialCompressedMacroActions(actions)) return actions;

  const expanded: ZhwxMacroAction[] = [];
  let lastPress: ZhwxMacroAction | null = null;

  for (const rec of actions) {
    if (rec.status === 1) {
      expanded.push({ ...rec });
      lastPress = rec;
      continue;
    }
    if (lastPress) {
      expanded.push({
        actionType: lastPress.actionType,
        keyCode: lastPress.keyCode,
        status: 0,
        delay: rec.delay,
      });
    }
  }

  if (lastPress) {
    const tail = expanded[expanded.length - 1];
    if (tail?.status === 1) {
      expanded.push({
        actionType: lastPress.actionType,
        keyCode: lastPress.keyCode,
        status: 0,
        delay: 1,
      });
    }
  }

  return expanded;
}

/** 将 UI 完整按下/抬起序列压缩为公版 Flash 格式 */
export function compressToOfficialMacroActions(
  actions: ZhwxMacroAction[]
): ZhwxMacroAction[] {
  if (isOfficialCompressedMacroActions(actions)) return actions;

  type Segment = {
    keyCode: number;
    actionType: number;
    holdMs: number;
    gapMs: number;
  };

  const segments: Segment[] = [];
  let i = 0;
  while (i < actions.length) {
    const press = actions[i];
    if (press.status !== 1) {
      i += 1;
      continue;
    }
    const next = actions[i + 1];
    const release =
      next && next.status === 0 && next.keyCode === press.keyCode ? next : null;
    segments.push({
      keyCode: press.keyCode,
      actionType: press.actionType,
      holdMs: press.delay,
      gapMs: release?.delay ?? press.delay,
    });
    i += release ? 2 : 1;
  }

  if (!segments.length) return [];

  const compressed: ZhwxMacroAction[] = [];
  for (let j = 0; j < segments.length; j++) {
    const seg = segments[j];
    const isLast = j === segments.length - 1;
    compressed.push({
      keyCode: seg.keyCode,
      actionType: seg.actionType,
      status: 1,
      delay: isLast ? 1 : seg.holdMs,
    });
    if (!isLast) {
      const nextSeg = segments[j + 1];
      compressed.push({
        keyCode: nextSeg.keyCode,
        actionType: nextSeg.actionType,
        status: 0,
        delay: seg.gapMs,
      });
    }
  }
  return compressed;
}

/** 读设备/UI 展示：先解析原始 4B 记录，再尝试公版压缩展开 */
export function expandZhwxMacroActionsForDisplay(
  actions: ZhwxMacroAction[]
): ZhwxMacroAction[] {
  if (isStandardFullMacroActions(actions)) return actions;
  return expandOfficialCompressedMacroActions(actions);
}

function writeActionRecord(
  data: Uint8Array,
  offset: number,
  action: ZhwxMacroAction
): void {
  const { keyType, keyValue } = uiKeyToMacroKeyType(
    action.keyCode,
    action.actionType
  );
  const delay = Math.max(0, Math.min(65535, Math.round(action.delay)));
  const actByte = standardStatusToActByte(action.status ? 1 : 0, keyType);
  // [delayL][delayH][keyValue][actByte]；bit7=1 按下(0x81) / bit7=0 弹起(0x01)
  data[offset] = delay & 0xff;
  data[offset + 1] = (delay >> 8) & 0xff;
  data[offset + 2] = keyValue & 0xff;
  data[offset + 3] = actByte;
}

/** 计算槽位数据区结束偏移（下一槽地址或 dataUsed 上界） */
function resolveMacroSlotBodyEnd(
  data: Uint8Array,
  macroId: number,
  slotAddr: number
): number {
  let nextAddr = ZHWX_MACRO_TAB_SIZE;
  for (let i = 0; i < ZHWX_MACRO_SLOT_COUNT; i++) {
    if (i === macroId) continue;
    const addr = readU16(data, ZHWX_MACRO_ADDR_OFFSET + i * 2);
    if (addr > slotAddr && addr < nextAddr) {
      nextAddr = addr;
    }
  }
  const dataUsed = readU16(data, 2);
  let dataEnd = ZHWX_MACRO_TAB_SIZE;
  if (dataUsed >= ZHWX_MACRO_DATA_BASE) {
    // 公版抓包 offset2=0x0800：表示整表有效长度上界
    dataEnd = Math.min(dataUsed, ZHWX_MACRO_TAB_SIZE);
  } else if (dataUsed > 0) {
    dataEnd = Math.min(ZHWX_MACRO_DATA_BASE + dataUsed, ZHWX_MACRO_TAB_SIZE);
  }
  return Math.min(nextAddr, dataEnd, ZHWX_MACRO_TAB_SIZE);
}

/**
 * 单宏槽数据区：2B 动作数 + 2B 保留 + 4B×N 动作序列。
 * 读：优先按 u16 动作数解析；失败时按 4B 步进扫描；再兼容旧版 12B 前缀 + 5B 动作。
 */
type ParseMacroBodyOptions = {
  /** 读设备时优先公版标准 4B [delayL][delayH][key][act]；为 true 时不回落旧交错格式 */
  standardActionsOnly?: boolean;
};

function parseMacroBody(
  data: Uint8Array,
  offset: number,
  endOffset?: number,
  options?: ParseMacroBodyOptions
): { actions: ZhwxMacroAction[]; bytesUsed: number } {
  const standardActionsOnly = options?.standardActionsOnly === true;
  if (offset + ZHWX_MACRO_SLOT_HEADER_SIZE > data.length) {
    return { actions: [], bytesUsed: 0 };
  }
  const bodyEnd = endOffset ?? data.length;
  const declaredCount = readU16(data, offset);

  const tryParseActions = (
    actionStart: number,
    count: number,
    step: number,
    parseOne: (buf: Uint8Array, off: number) => ZhwxMacroAction | null,
    plausible: (buf: Uint8Array, off: number) => boolean
  ) => {
    const parsed: ZhwxMacroAction[] = [];
    let cursor = actionStart;
    for (let i = 0; i < count; i++) {
      if (cursor + step > bodyEnd) break;
      if (!plausible(data, cursor)) break;
      const act = parseOne(data, cursor);
      if (!act) break;
      parsed.push(act);
      cursor += step;
    }
    return { parsed, cursor };
  };

  if (declaredCount > 0 && declaredCount <= 512) {
    const standardLayout = tryParseActions(
      offset + ZHWX_MACRO_SLOT_HEADER_SIZE,
      declaredCount,
      ZHWX_MACRO_ACTION_RECORD_SIZE,
      parseStandardActionRecord,
      isStandardMacroActionRecord
    );
    if (standardLayout.parsed.length === declaredCount) {
      return {
        actions: standardLayout.parsed,
        bytesUsed: standardLayout.cursor - offset,
      };
    }

    const tightLayout = tryParseActions(
      offset + 2,
      declaredCount,
      ZHWX_MACRO_ACTION_RECORD_SIZE,
      parseStandardActionRecord,
      isStandardMacroActionRecord
    );
    if (tightLayout.parsed.length === declaredCount) {
      return {
        actions: tightLayout.parsed,
        bytesUsed: tightLayout.cursor - offset,
      };
    }

    const officialLayout = tryParseActions(
      offset + ZHWX_OFFICIAL_MACRO_SLOT_PREFIX_SIZE,
      declaredCount,
      ZHWX_MACRO_ACTION_RECORD_SIZE,
      parseStandardActionRecord,
      isStandardMacroActionRecord
    );
    if (officialLayout.parsed.length === declaredCount) {
      return {
        actions: officialLayout.parsed,
        bytesUsed: officialLayout.cursor - offset,
      };
    }

    const strictFallback = scanMacroBodyWithFallbacks(
      data,
      offset,
      bodyEnd,
      true,
      declaredCount
    );
    if (strictFallback.actions.length > 0) {
      return strictFallback;
    }

    if (standardActionsOnly) {
      return { actions: [], bytesUsed: 0 };
    }

    const interleavedLayout = tryParseActions(
      offset + ZHWX_MACRO_SLOT_HEADER_SIZE,
      declaredCount,
      ZHWX_MACRO_ACTION_RECORD_SIZE,
      parseInterleavedActionRecord,
      isInterleavedMacroActionRecord
    );
    if (interleavedLayout.parsed.length === declaredCount) {
      return {
        actions: interleavedLayout.parsed,
        bytesUsed: interleavedLayout.cursor - offset,
      };
    }

    const staggeredLayout = tryParseActions(
      offset + ZHWX_MACRO_SLOT_HEADER_SIZE,
      declaredCount,
      ZHWX_MACRO_ACTION_RECORD_SIZE,
      parseStaggeredActionRecord,
      isStaggeredMacroActionRecord
    );
    if (staggeredLayout.parsed.length === declaredCount) {
      return {
        actions: staggeredLayout.parsed,
        bytesUsed: staggeredLayout.cursor - offset,
      };
    }

    const legacy12 = tryParseActions(
      offset + 12,
      declaredCount,
      5,
      parseLegacyActionRecord,
      isLegacyMacroActionRecord
    );
    if (legacy12.parsed.length > 0) {
      return {
        actions: legacy12.parsed,
        bytesUsed: legacy12.cursor - offset,
      };
    }
  }

  const fallback = scanMacroBodyWithFallbacks(
    data,
    offset,
    bodyEnd,
    standardActionsOnly,
    declaredCount
  );
  if (fallback.actions.length > 0) {
    return fallback;
  }

  return { actions: [], bytesUsed: 0 };
}

/** 从 base macro_tab 原样复制指定槽位数据区（保留未编辑槽的固件原始字节） */
function copyMacroSlotRawFromBase(
  data: Uint8Array,
  base: Uint8Array,
  macroId: number,
  destOffset: number
): number {
  const addr = readU16(base, ZHWX_MACRO_ADDR_OFFSET + macroId * 2);
  if (addr < ZHWX_MACRO_DATA_BASE) return 0;
  const bodyEnd = resolveMacroSlotBodyEnd(base, macroId, addr);
  const len = bodyEnd - addr;
  if (len <= 0 || destOffset + len > ZHWX_MACRO_TAB_SIZE) return 0;
  data.set(base.subarray(addr, bodyEnd), destOffset);
  return len;
}

function writeMacroBody(
  data: Uint8Array,
  offset: number,
  actions: ZhwxMacroAction[]
): number {
  writeU16(data, offset, actions.length);
  writeU16(data, offset + 2, 0);
  let cursor = offset + ZHWX_MACRO_SLOT_HEADER_SIZE;
  for (const action of actions) {
    writeActionRecord(data, cursor, action);
    cursor += ZHWX_MACRO_ACTION_RECORD_SIZE;
  }
  return cursor - offset;
}

/** 写入设备的动作序列：完整按下/抬起。槽头为 u16 动作数量 + u16 保留。 */
export function encodeZhwxMacroWireActions(
  actions: ZhwxMacroAction[]
): ZhwxMacroAction[] {
  return normalizeZhwxMacroActionsForDevice(actions);
}

/** 协议槽体：u16 动作数量 + u16 保留 + 4B 动作，从 addr+4 开始 */
function writeOfficialMacroBody(
  data: Uint8Array,
  offset: number,
  actions: ZhwxMacroAction[]
): number {
  const wireActions = encodeZhwxMacroWireActions(actions);
  return writeMacroBody(data, offset, wireActions);
}

/** 生成单槽宏动作二进制（2B 动作数 + 2B 保留 + 4B×N，不含 macro_tab 全局头） */
export function buildZhwxMacroSlotBodyBytes(
  actions: ZhwxMacroAction[]
): Uint8Array {
  const buf = new Uint8Array(
    ZHWX_MACRO_SLOT_HEADER_SIZE + actions.length * ZHWX_MACRO_ACTION_RECORD_SIZE
  );
  writeMacroBody(buf, 0, actions);
  return buf;
}

/** 生成完整 macro_tab（2048B），等价于 buildZhwxMacroTab(null, updates) */
export function buildZhwxMacroData(
  updates: Array<{
    macroId: number;
    actions: ZhwxMacroAction[];
    intervalMode?: ZhwxMacroIntervalMode;
    fixedIntervalMs?: number;
  }>,
  base: Uint8Array | null = null
): Uint8Array {
  return buildZhwxMacroTab(base, updates);
}

export type ParseZhwxMacroTabOptions = {
  /** 读设备/UI 展示时建议开启，只解析标准 4B 动作 */
  standardActionsOnly?: boolean;
};

/** 解析完整 macro_tab */
export function parseZhwxMacroTab(
  raw: Uint8Array,
  options?: ParseZhwxMacroTabOptions
): ZhwxParsedMacroTab {
  const standardActionsOnly = options?.standardActionsOnly === true;
  const data =
    raw.length >= ZHWX_MACRO_TAB_SIZE
      ? raw.slice(0, ZHWX_MACRO_TAB_SIZE)
      : (() => {
          const padded = new Uint8Array(ZHWX_MACRO_TAB_SIZE);
          padded.set(raw, 0);
          return padded;
        })();

  const slots: ZhwxParsedMacroTab["slots"] = [];
  for (let macroId = 0; macroId < ZHWX_MACRO_SLOT_COUNT; macroId++) {
    const addr = readU16(data, ZHWX_MACRO_ADDR_OFFSET + macroId * 2);
    const used = isSlotUsed(data, macroId) && addr >= ZHWX_MACRO_DATA_BASE;
    if (!used) {
      slots.push({
        macroId,
        valid: false,
        actions: [],
        intervalMode: 0,
        fixedIntervalMs: 1,
      });
      continue;
    }
    const bodyEnd = resolveMacroSlotBodyEnd(data, macroId, addr);
    const body = parseMacroBody(data, addr, bodyEnd, { standardActionsOnly });
    const intervalPref = readZhwxMacroIntervalPref(macroId);
    slots.push({
      macroId,
      valid: body.actions.length > 0,
      actions: expandZhwxMacroActionsForDisplay(body.actions),
      intervalMode: intervalPref.intervalMode,
      fixedIntervalMs: intervalPref.fixedIntervalMs,
    });
  }
  return { raw: data, slots };
}

/**
 * 读设备回显专用：先按公版标准 4B 解析；槽位在 tab 上有效但标准解析为空时，
 * 再尝试交错/旧格式（公版 PC 工具写入的 macro_tab 可能与 strict 判定不完全一致）。
 */
export function parseZhwxMacroTabForDisplay(
  raw: Uint8Array
): ZhwxParsedMacroTab {
  const strict = parseZhwxMacroTab(raw, { standardActionsOnly: true });
  const loose = parseZhwxMacroTab(raw, { standardActionsOnly: false });
  const slots = strict.slots.map((slot, macroId) => {
    if (slot.valid && slot.actions.length > 0) return slot;
    if (!isZhwxMacroSlotReadyOnTab(strict.raw, macroId)) return slot;
    const fallback = loose.slots[macroId];
    if (fallback?.actions.length) {
      return {
        ...fallback,
        macroId,
        valid: true,
        intervalMode: slot.intervalMode,
        fixedIntervalMs: slot.fixedIntervalMs,
      };
    }
    const rescued = parseZhwxMacroSlotActionsFromTab(strict.raw, macroId, {
      standardActionsOnly: false,
    });
    if (rescued.length) {
      return {
        ...slot,
        macroId,
        valid: true,
        actions: expandZhwxMacroActionsForDisplay(rescued),
      };
    }
    return slot;
  });
  return { raw: strict.raw, slots };
}

/** 固定间隔：除末项外 delay 设为 fixedMs；末项保持 1ms（与公版末键抬起一致） */
export function applyZhwxMacroFixedInterval(
  actions: ZhwxMacroAction[],
  fixedMs: number
): ZhwxMacroAction[] {
  const ms = Math.max(1, Math.min(65535, Math.round(fixedMs)));
  if (!actions.length) return actions;
  return actions.map((a, index) => ({
    ...a,
    delay: index === actions.length - 1 ? Math.max(1, a.delay) : ms,
  }));
}

/**
 * 写入设备前规范化宏动作：过滤无效键码，并将 UI 键码转为协议 HID keyValue 再还原为 UI 键码，
 * 避免从设备读回的交错格式污染（如 keyValue 0x76 被当成 UI 118）持续写回。
 *
 * 与公版 PC 工具 / Bus Hound 抓包一致的顺序与 delay 语义（录制页已按此生成）：
 * - 每条键：先按下(status=1) 后抬起(status=0)，按录制顺序排列，不重排
 * - 按下项 delay = 按住时长(ms)；抬起项 delay = 到下一次按下的间隔(ms)
 * - 末项 delay 至少 1ms（公版末项常为 0x0001）
 * - 编码为 [delayL][delayH][keyValue][actByte]，0x01 按下 / 0x81 弹起
 */
export function normalizeZhwxMacroActionsForDevice(
  actions: ZhwxMacroAction[]
): ZhwxMacroAction[] {
  const cleaned = (actions ?? [])
    .map((action) => {
      const keyCode = Number(action.keyCode) || 0;
      if (keyCode <= 0) return null;
      const actionType = Number(action.actionType) || 3;
      const { keyType, keyValue } = uiKeyToMacroKeyType(keyCode, actionType);
      if (
        keyType === ZHWX_MACRO_ACT_KB &&
        !isPlausibleMacroKbKeyValue(keyValue)
      ) {
        return null;
      }
      const ui = macroKeyToUi(keyType, keyValue, actionType);
      return {
        delay: Math.max(
          0,
          Math.min(65535, Math.round(Number(action.delay) || 0))
        ),
        status: Number(action.status) ? 1 : 0,
        actionType: ui.actionType,
        keyCode: ui.keyCode,
      };
    })
    .filter(
      (item): item is ZhwxMacroAction => item != null && item.keyCode > 0
    );

  if (!cleaned.length) return cleaned;

  return cleaned.map((action, index) => ({
    ...action,
    status: action.status ? 1 : 0,
    delay:
      index === cleaned.length - 1 ? Math.max(1, action.delay) : action.delay,
  }));
}

/** 写入 macro_tab（保留其它槽位，仅更新传入的槽） */
export function buildZhwxMacroTab(
  base: Uint8Array | null,
  updates: Array<{
    macroId: number;
    actions: ZhwxMacroAction[];
    intervalMode?: ZhwxMacroIntervalMode;
    fixedIntervalMs?: number;
  }>
): Uint8Array {
  const data =
    base && base.length >= ZHWX_MACRO_TAB_SIZE
      ? new Uint8Array(base.slice(0, ZHWX_MACRO_TAB_SIZE))
      : new Uint8Array(ZHWX_MACRO_TAB_SIZE);

  writeU16(data, 4, ZHWX_MACRO_SLOT_COUNT);

  const updateMap = new Map<
    number,
    {
      actions: ZhwxMacroAction[];
      intervalMode: ZhwxMacroIntervalMode;
      fixedIntervalMs: number;
    }
  >();
  for (const update of updates) {
    if (update.macroId < 0 || update.macroId >= ZHWX_MACRO_SLOT_COUNT) continue;
    const pref = readZhwxMacroIntervalPref(update.macroId);
    const intervalMode = update.intervalMode ?? pref.intervalMode;
    const fixedIntervalMs = update.fixedIntervalMs ?? pref.fixedIntervalMs;
    let actions = normalizeZhwxMacroActionsForDevice(update.actions ?? []);
    if (intervalMode === 1 && actions.length) {
      actions = applyZhwxMacroFixedInterval(actions, fixedIntervalMs);
    }
    // UI 为完整 press/release；按协议写入 u16 动作数量 + 2B 保留 + 4B 动作
    updateMap.set(update.macroId, { actions, intervalMode, fixedIntervalMs });
  }

  const baseTab = base && base.length >= ZHWX_MACRO_TAB_SIZE ? base : null;

  writeU16(data, 0, 0);
  data[6] = 0;
  for (let i = 0; i < ZHWX_MACRO_SLOT_COUNT; i++) {
    writeU16(data, ZHWX_MACRO_ADDR_OFFSET + i * 2, 0);
  }
  // 清空数据区，避免 merge 旧 macro_tab 时在槽位间隙残留交错格式字节被固件误读
  data.fill(0, ZHWX_MACRO_DATA_BASE, ZHWX_MACRO_TAB_SIZE);

  let cursor = ZHWX_MACRO_DATA_BASE;
  for (let macroId = 0; macroId < ZHWX_MACRO_SLOT_COUNT; macroId++) {
    const update = updateMap.get(macroId);
    if (update) {
      if (!update.actions.length) continue;
      writeU16(data, ZHWX_MACRO_ADDR_OFFSET + macroId * 2, cursor);
      const used = writeOfficialMacroBody(data, cursor, update.actions);
      cursor += used;
    } else if (baseTab) {
      const used = copyMacroSlotRawFromBase(data, baseTab, macroId, cursor);
      if (used > 0) {
        writeU16(data, ZHWX_MACRO_ADDR_OFFSET + macroId * 2, cursor);
        cursor += used;
      }
    }
    if (cursor >= ZHWX_MACRO_TAB_SIZE) break;
  }

  // 协议 offset 0~1 为是否使用标志。有宏时写魔数 0x55AA（字节 AA 55）。
  // 槽是否占用看地址表。把槽 bit 或进魔数会把 M0 写成 0x55AB，低字节不再是 0xAA。
  if (cursor > ZHWX_MACRO_DATA_BASE) {
    writeU16(data, 0, ZHWX_MACRO_TAB_MAGIC);
  }
  writeU16(data, 2, ZHWX_MACRO_TAB_SIZE);
  return data;
}

export function zhwxMacroSlotSummaries(
  parsed: ZhwxParsedMacroTab
): ZhwxMacroSlotSummary[] {
  return parsed.slots.map((slot) => ({
    macroId: slot.macroId,
    valid: slot.valid,
    actNum: slot.actions.length,
    intervalMode: slot.intervalMode,
    fixedIntervalMs: slot.fixedIntervalMs,
  }));
}

/** 当前连接设备的宏缓存范围：型号 + VID + PID，避免 DK87 与 DK75 Single 共用一份本地宏 */
export function resolveZhwxMacroDeviceScope(): string {
  try {
    const eq = getTabScopedEquipmentData() ?? {};
    const vendorId = Number(eq.vendorId);
    const productId = Number(eq.productId);
    const keyboardType = String(eq.keyboardType ?? "").trim() || "unknown";
    const vid = Number.isFinite(vendorId) ? vendorId.toString(16) : "0";
    const pid = Number.isFinite(productId) ? productId.toString(16) : "0";
    return `${keyboardType}:${vid}:${pid}`;
  } catch {
    return "unknown:0:0";
  }
}

/** 宏槽间隔/停止方式偏好，按设备隔离 */
export function getZhwxMacroStorageKey(): string {
  return `zhwxMacroIntervalData:${resolveZhwxMacroDeviceScope()}`;
}

/** 宏槽动作缓存，按设备隔离 */
export function getZhwxMacroLocalStorageKey(): string {
  return `zhwxMacroSlotData:${resolveZhwxMacroDeviceScope()}`;
}

function normalizeZhwxMacroSlotPref(
  pref: Partial<ZhwxMacroSlotPref> | null | undefined
): ZhwxMacroSlotPref {
  if (!pref || typeof pref !== "object") {
    return { ...DEFAULT_ZHWX_MACRO_SLOT_PREF };
  }
  const stopRaw = Number(pref.stopMethod);
  const stopMethod = (
    stopRaw >= 0 && stopRaw <= 3 ? stopRaw : 0
  ) as ZhwxMacroStopMethod;
  return {
    intervalMode: pref.intervalMode === 1 ? 1 : 0,
    fixedIntervalMs: Math.max(1, Number(pref.fixedIntervalMs) || 1),
    bindMode: pref.bindMode === "count" ? "count" : "stop",
    stopMethod,
    playCount: Math.max(
      1,
      Math.min(255, Math.round(Number(pref.playCount) || 1))
    ),
  };
}

/** 读取宏槽 UI 偏好（间隔 + 改键绑定；设备 macro_tab 不存这些字段） */
export function readZhwxMacroSlotPref(macroId: number): ZhwxMacroSlotPref {
  if (typeof localStorage === "undefined") {
    return { ...DEFAULT_ZHWX_MACRO_SLOT_PREF };
  }
  try {
    const raw = localStorage.getItem(getZhwxMacroStorageKey());
    if (!raw) return { ...DEFAULT_ZHWX_MACRO_SLOT_PREF };
    const map = JSON.parse(raw) as Record<
      string,
      Partial<ZhwxMacroSlotPref> | null
    >;
    return normalizeZhwxMacroSlotPref(map[String(macroId)]);
  } catch {
    return { ...DEFAULT_ZHWX_MACRO_SLOT_PREF };
  }
}

/** 读取 UI 间隔模式偏好（设备 macro_tab 不存该字段） */
export function readZhwxMacroIntervalPref(macroId: number): {
  intervalMode: ZhwxMacroIntervalMode;
  fixedIntervalMs: number;
} {
  const pref = readZhwxMacroSlotPref(macroId);
  return {
    intervalMode: pref.intervalMode,
    fixedIntervalMs: pref.fixedIntervalMs,
  };
}

/** 写入宏槽 UI 偏好（局部更新） */
export function writeZhwxMacroSlotPref(
  macroId: number,
  patch: Partial<ZhwxMacroSlotPref>
): void {
  if (typeof localStorage === "undefined") return;
  try {
    const storageKey = getZhwxMacroStorageKey();
    const raw = localStorage.getItem(storageKey);
    const map = raw
      ? (JSON.parse(raw) as Record<string, Partial<ZhwxMacroSlotPref> | null>)
      : {};
    const next = normalizeZhwxMacroSlotPref({
      ...readZhwxMacroSlotPref(macroId),
      ...patch,
    });
    map[String(macroId)] = next;
    localStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/** 写入 UI 间隔模式偏好 */
export function writeZhwxMacroIntervalPref(
  macroId: number,
  intervalMode: ZhwxMacroIntervalMode,
  fixedIntervalMs: number
): void {
  writeZhwxMacroSlotPref(macroId, { intervalMode, fixedIntervalMs });
}

/** 写入改键绑定偏好（0x50 停止方式 / 0x51 播放次数） */
export function writeZhwxMacroBindPref(
  macroId: number,
  bindMode: ZhwxMacroBindMode,
  stopMethod: ZhwxMacroStopMethod,
  playCount: number
): void {
  writeZhwxMacroSlotPref(macroId, { bindMode, stopMethod, playCount });
}

/** 清除宏槽位本地偏好（清除宏时调用） */
export function clearZhwxMacroIntervalPref(macroId: number): void {
  if (typeof localStorage === "undefined") return;
  try {
    const storageKey = getZhwxMacroStorageKey();
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    const map = JSON.parse(raw) as Record<string, unknown>;
    map[String(macroId)] = null;
    localStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}
